import { useAppSelector } from "~/hooks";
import { GeneratingSpinner } from "../components/GeneratingSpinner";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { SectionWrapper } from "../components/SectionWrapper";
import { StaleWarning } from "../components/StaleWarning";
import type { SectionStatus } from "../hooks/usePageViewData";
import styles from "./FunctionalRequirementsSection.module.scss";

type FunctionalRequirementsSectionProps = {
  number: number;
  status: SectionStatus;
  detailPath: string;
  onGenerate: (documentType: string) => void;
  canRegenerate: boolean;
};

const MAX_ROWS = 10;

const TABLE_COLUMNS = ["業務", "機能", "利用ユーザー", "実装方法", "備考"] as const;

export const FunctionalRequirementsSection = ({
  number,
  status,
  detailPath,
  onGenerate,
  canRegenerate,
}: FunctionalRequirementsSectionProps) => {
  const { data, isMarkdown } = useAppSelector((s) => s.functionalRequirement);

  const renderContent = () => {
    if (isMarkdown) {
      return (
        <div className={styles["fallback"]}>
          テーブル形式で表示できません。詳細ページで確認してください →
        </div>
      );
    }

    if (data.length === 0) {
      return <div className={styles["fallback"]}>機能要件データがありません</div>;
    }

    const visibleRows = data.slice(0, MAX_ROWS);
    const remainingCount = data.length - MAX_ROWS;

    return (
      <>
        <div className={styles["tableWrapper"]}>
          <table className={styles["table"]}>
            <thead>
              <tr>
                {TABLE_COLUMNS.map((col) => (
                  <th key={col} className={styles["th"]}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={row.fid} className={index % 2 === 1 ? styles["trEven"] : styles["trOdd"]}>
                  {TABLE_COLUMNS.map((col) => (
                    <td key={col} className={styles["td"]}>
                      {row[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {remainingCount > 0 && (
          <p className={styles["moreMessage"]}>他 {remainingCount} 件は詳細ページで確認 →</p>
        )}
      </>
    );
  };

  if (status === "generating") {
    return (
      <SectionWrapper
        number={number}
        title="主要機能一覧"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("functional-requirements")}
        disableGenerate={!canRegenerate}
      >
        <GeneratingSpinner />
      </SectionWrapper>
    );
  }

  if (status === "stale") {
    return (
      <SectionWrapper
        number={number}
        title="主要機能一覧"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("functional-requirements")}
        disableGenerate={!canRegenerate}
      >
        <SectionErrorBoundary sectionName="主要機能一覧" detailPath={detailPath}>
          {renderContent()}
        </SectionErrorBoundary>
        <StaleWarning />
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      number={number}
      title="主要機能一覧"
      status={status}
      detailPath={detailPath}
      onGenerate={() => onGenerate("functional-requirements")}
      disableGenerate={!canRegenerate}
    >
      <SectionErrorBoundary sectionName="主要機能一覧" detailPath={detailPath}>
        {renderContent()}
      </SectionErrorBoundary>
    </SectionWrapper>
  );
};
